import { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import JsonMonaco from "../../../components/dashboardSchemaEditor/JsonMonaco";
import {
    AbaAddButton,
    AbaEditPopover,
    useAbasCatalogo,
} from "../../../components/abaSchemaSelector";
import {
    abasDisponiveis,
    contarSlotsDoDraft,
    denormalizeSchema,
    emptyDraft,
    normalizeSchema,
    novaAba,
    novoSlot,
} from "../../../utils/dashboardSchemaDraft";
import AbaCard from "./abaCard";
import SchemaHelpPopover from "./schemaHelpPopover";

function textoDoDraft(draft) {
    const objeto = denormalizeSchema(draft);
    return objeto ? JSON.stringify(objeto, null, 2) : "";
}

// Formulário estruturado do dashboard_schema. Dono de um objeto (o draft),
// não de um texto — o editor de JSON cru fica como saída de emergência.
export default function SchemaBuilder({
    draft,
    setDraft,
    editar,
    dashboardSchema,
    avancado,
    setAvancado,
    avancadoTexto,
    setAvancadoTexto,
    avancadoErro,
    setAvancadoErro,
    sugestoesItens = [],
    notify,
    onAbrirBulk,
    podeAplicarEmMassa = false,
    styles,
}) {
    const theme = useTheme();
    const [fechadas, setFechadas] = useState(() => new Set());
    const [editandoAba, setEditandoAba] = useState(null);

    const { abas: catalogoAbas } = useAbasCatalogo(notify);

    const disponiveis = useMemo(
        () => abasDisponiveis(draft, catalogoAbas),
        [draft, catalogoAbas],
    );
    const totalSlots = useMemo(() => contarSlotsDoDraft(draft), [draft]);

    /* ---------------------------- mutadores ---------------------------- */

    const mapAba = (abaId, fn) =>
        setDraft((prev) => ({
            ...prev,
            abas: prev.abas.map((aba) => (aba.id === abaId ? fn(aba) : aba)),
        }));

    const adicionarAba = (key) =>
        setDraft((prev) =>
            prev.abas.some((aba) => aba.key === key)
                ? prev
                : { ...prev, abas: [...prev.abas, novaAba(key)] },
        );

    const removerAba = (abaId) =>
        setDraft((prev) => ({
            ...prev,
            abas: prev.abas.filter((aba) => aba.id !== abaId),
        }));

    const adicionarSlot = (abaId) =>
        mapAba(abaId, (aba) => ({ ...aba, slots: [...aba.slots, novoSlot()] }));

    const removerSlot = (abaId, slotId) =>
        mapAba(abaId, (aba) => ({
            ...aba,
            slots: aba.slots.filter((slot) => slot.id !== slotId),
        }));

    const alterarSlot = (abaId, slotId, patch) =>
        mapAba(abaId, (aba) => ({
            ...aba,
            slots: aba.slots.map((slot) =>
                slot.id === slotId ? { ...slot, ...patch } : slot,
            ),
        }));

    const moverSlot = (abaId, slotId, direcao) =>
        mapAba(abaId, (aba) => {
            const indice = aba.slots.findIndex((slot) => slot.id === slotId);
            const destino = indice + direcao;
            if (indice < 0 || destino < 0 || destino >= aba.slots.length) return aba;
            const slots = [...aba.slots];
            const [movido] = slots.splice(indice, 1);
            slots.splice(destino, 0, movido);
            return { ...aba, slots };
        });

    const toggleAba = (abaId) =>
        setFechadas((prev) => {
            const proxima = new Set(prev);
            if (proxima.has(abaId)) proxima.delete(abaId);
            else proxima.add(abaId);
            return proxima;
        });

    /* ------------------------- JSON avançado --------------------------- */

    const entrarAvancado = () => {
        setAvancadoTexto(textoDoDraft(draft));
        setAvancadoErro(null);
        setAvancado(true);
    };

    const sairAvancado = () => {
        if (avancadoErro) return; // bloqueado: o draft ficaria dessincronizado
        setAvancado(false);
    };

    const descartarAvancado = () => {
        setAvancadoTexto(textoDoDraft(draft));
        setAvancadoErro(null);
    };

    const alterarTextoAvancado = (texto) => {
        setAvancadoTexto(texto);

        const limpo = String(texto ?? "").trim();
        if (!limpo) {
            setDraft(emptyDraft());
            setAvancadoErro(null);
            return;
        }

        try {
            const parsed = JSON.parse(limpo);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                setAvancadoErro("O schema precisa ser um objeto JSON.");
                return;
            }
            setDraft(normalizeSchema(parsed));
            setAvancadoErro(null);
        } catch (erro) {
            setAvancadoErro(erro.message);
        }
    };

    /* ---------------------------- leitura ------------------------------ */

    // reusa os mesmos cards do modo edição, sem controles
    const draftLeitura = useMemo(
        () => (editar ? null : normalizeSchema(dashboardSchema)),
        [editar, dashboardSchema],
    );

    if (!editar) {
        const abasLeitura = draftLeitura?.abas || [];

        if (abasLeitura.length === 0) {
            return (
                <Box sx={styles.emptyStateBox} minHeight="200px">
                    <Chip
                        sx={{
                            bgcolor: theme.palette.chip.main.arquive,
                            color: theme.palette.white.main,
                            width: "150px",
                        }}
                        label="Sem Schema"
                    />
                </Box>
            );
        }

        const slotsLeitura = contarSlotsDoDraft(draftLeitura);

        return (
            <Stack direction="column" gap={1.5} width="100%">
                <Box sx={styles.schemaHeader}>
                    <Typography sx={styles.schemaTitulo}>Métricas do schema</Typography>
                    <Typography sx={styles.schemaContagem}>
                        {slotsLeitura} {slotsLeitura === 1 ? "métrica" : "métricas"} em{" "}
                        {abasLeitura.length} {abasLeitura.length === 1 ? "aba" : "abas"}
                    </Typography>
                    <SchemaHelpPopover catalogoAbas={catalogoAbas} />

                    <Box flex={1} />

                    {/* {typeof draftLeitura.version === "number" && (
                        <Typography sx={styles.schemaContagem}>
                            versão {draftLeitura.version}
                        </Typography>
                    )} */}
                </Box>

                {abasLeitura.map((aba) => (
                    <AbaCard
                        key={aba.id}
                        aba={aba}
                        catalogoAbas={catalogoAbas}
                        aberta={!fechadas.has(aba.id)}
                        styles={styles}
                        somenteLeitura
                        onToggle={() => toggleAba(aba.id)}
                    />
                ))}
            </Stack>
        );
    }

    /* ---------------------------- edição ------------------------------- */

    const totalAbas = draft.abas.length;

    return (
        <Stack direction="column" gap={1.5} width="100%">
            <Box sx={styles.schemaHeader}>
                <Typography sx={styles.schemaTitulo}>Métricas do schema</Typography>
                <Typography sx={styles.schemaContagem}>
                    {totalSlots} {totalSlots === 1 ? "métrica" : "métricas"} em {totalAbas}{" "}
                    {totalAbas === 1 ? "aba" : "abas"}
                </Typography>
                <SchemaHelpPopover catalogoAbas={catalogoAbas} />

                <Box flex={1} />

                <Tooltip
                    title={
                        podeAplicarEmMassa
                            ? "Aplicar este schema a outros modelos"
                            : "Preencha ao menos uma métrica para aplicar em outros modelos"
                    }
                >
                    <span>
                        <Button
                            size="small"
                            onClick={onAbrirBulk}
                            disabled={!podeAplicarEmMassa}
                            sx={styles.schemaAcao}
                        >
                            Aplicar a outros modelos
                        </Button>
                    </span>
                </Tooltip>

                <Button
                    size="small"
                    onClick={avancado ? sairAvancado : entrarAvancado}
                    disabled={avancado && Boolean(avancadoErro)}
                    sx={styles.schemaAcao}
                >
                    {avancado ? "Voltar ao formulário" : "JSON avançado"}
                </Button>
            </Box>

            {avancado ? (
                <>
                    <Alert severity="info" sx={styles.alertaAvancado}>
                        Edição direta do JSON. O formulário acompanha o que você digita
                        enquanto o JSON estiver válido.
                    </Alert>

                    <JsonMonaco
                        value={avancadoTexto}
                        onChange={alterarTextoAvancado}
                        autoHeight
                        minHeight={200}
                    />

                    {avancadoErro && (
                        <Alert
                            severity="error"
                            sx={styles.alertaAvancado}
                            action={
                                <Button size="small" onClick={descartarAvancado}>
                                    Descartar alterações do JSON
                                </Button>
                            }
                        >
                            JSON inválido: {avancadoErro}
                            <Typography variant="caption" display="block">
                                Não é possível voltar ao formulário nem salvar enquanto
                                isto não for corrigido.
                            </Typography>
                        </Alert>
                    )}
                </>
            ) : (
                <>
                    {draft.abas.length === 0 && (
                        <Box sx={styles.emptyStateBox} minHeight="140px">
                            <Typography variant="body2" color="text.disabled">
                                Nenhuma aba configurada.
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                Adicione uma aba para começar a montar o dashboard.
                            </Typography>
                        </Box>
                    )}

                    {draft.abas.map((aba) => (
                        <AbaCard
                            key={aba.id}
                            aba={aba}
                            catalogoAbas={catalogoAbas}
                            aberta={!fechadas.has(aba.id)}
                            styles={styles}
                            sugestoes={sugestoesItens}
                            notify={notify}
                            onToggle={() => toggleAba(aba.id)}
                            onRemover={() => removerAba(aba.id)}
                            onEditarAba={(anchorEl, entrada) =>
                                setEditandoAba({ anchorEl, aba: entrada })
                            }
                            onAdicionarSlot={() => adicionarSlot(aba.id)}
                            onRemoverSlot={(slotId) => removerSlot(aba.id, slotId)}
                            onMoverSlot={(slotId, direcao) =>
                                moverSlot(aba.id, slotId, direcao)
                            }
                            onChangeSlotName={(slotId, nome) =>
                                alterarSlot(aba.id, slotId, { name: nome })
                            }
                            onChangeSlotItems={(slotId, itens) =>
                                alterarSlot(aba.id, slotId, { items: itens })
                            }
                        />
                    ))}

                    <AbaAddButton
                        disponiveis={disponiveis}
                        onAdicionar={adicionarAba}
                        notify={notify}
                    />

                    <AbaEditPopover
                        estado={editandoAba}
                        onClose={() => setEditandoAba(null)}
                        notify={notify}
                    />
                </>
            )}
        </Stack>
    );
}
